import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { Css, H6, Responsive } from '../../../../ui';
import FlightSearchShimmer from './FlightSearchShimmer';
import { Color } from '../../../../styles/Color';
import FlightSearchDayGroups from './FlightSearchDayGroups';
import { QuickLink } from '../../../../components/common/quick-links/QuickLink';
import { getFlightsListUrl } from '../../../flights/use-cases/getFlightsListUrl';
import { FlightListStore } from '../../../flights/overview/domain/stores/FlightListStore';

interface IFlightSearchListProps {
	store: FlightListStore;
	query?: string;
	isDebouncing: boolean;
}

export const FlightSearchList = observer(
	({ store, query, isDebouncing }: IFlightSearchListProps) => {
		const { flights, isLoading, error } = store;
		const showLoading = isLoading || isDebouncing;

		const flightPageQuery = flights.length > 0 ? query : undefined;
		const flightPageUrl = getFlightsListUrl(undefined, flightPageQuery);

		const flightsCount = showLoading ? '' : `(${flights.length})`;

		if (error && !showLoading) return null; // error state
		if (flights.length === 0 && !showLoading) return null; // empty state
		return (
			<div>
				<Heading>
					<ListTitle>Flights {flightsCount}</ListTitle>
					{showLoading && <FlightPageLink url={flightPageUrl} />}
				</Heading>
				{showLoading ? (
					<FlightSearchShimmer />
				) : (
					<FlightSearchDayGroups
						flights={flights}
						topRightSlot={<FlightPageLink url={flightPageUrl} />}
					/>
				)}
			</div>
		);
	},
);

function FlightPageLink({ url }: { url: string }) {
	return (
		<StyledLink type="internal" url={url}>
			Go to flight page
		</StyledLink>
	);
}

const Heading = styled.div`
	width: 100%;
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: ${Css.pxToRem(12)};
`;

const ListTitle = styled(H6)`
	margin-bottom: 0;
	color: ${Color.PrimaryBlue};

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		font-size: ${Css.pxToRem(16)};
	}
`;

const StyledLink = styled(QuickLink)`
	margin: 0;
`;
